import * as React from "react"
import { cn } from "@/lib/utils"

export interface NfcWirelessSvgProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon in pixels
   * @default 18
   */
  size?: number
}

/**
 * NFC Wireless icon as SVG
 *
 * This icon represents NFC wireless scanning/proximity communication,
 * used to indicate NFC chip pairing functionality.
 * Simple design optimized for small sizes (w-4 h-4).
 *
 * @example
 * ```tsx
 * <NfcWirelessSvg />
 * <NfcWirelessSvg size={24} className="text-black" />
 * <NfcWirelessSvg className="w-4 h-4" />
 * ```
 */
export const NfcWirelessSvg = React.forwardRef<SVGSVGElement, NfcWirelessSvgProps>(
  ({ size = 18, className, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn("inline-flex shrink-0", className)}
        aria-hidden="true"
        {...props}
      >
        {/* NFC tap symbol - hand with wireless waves */}
        {/* Outer wave left */}
        <path
          d="M4 8C2.5 9.5 1.5 11.5 1.5 14C1.5 16.5 2.5 18.5 4 20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Inner wave left */}
        <path
          d="M7 10C6 11 5.5 12.5 5.5 14C5.5 15.5 6 17 7 18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Outer wave right */}
        <path
          d="M20 8C21.5 9.5 22.5 11.5 22.5 14C22.5 16.5 21.5 18.5 20 20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Inner wave right */}
        <path
          d="M17 10C18 11 18.5 12.5 18.5 14C18.5 15.5 18 17 17 18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Center device/tag */}
        <rect
          x="9"
          y="4"
          width="6"
          height="16"
          rx="1.5"
          fill="currentColor"
        />
        {/* NFC indicator on device */}
        <circle cx="12" cy="8" r="1.5" fill="white" />
      </svg>
    )
  }
)

NfcWirelessSvg.displayName = "NfcWirelessSvg"
